/**
 * Emojis curados do seletor do chat, com nomes em português para a pesquisa.
 *
 * Sem dependências: uma constante, ~230 emojis em cinco categorias
 * (sorrisos, gestos e pessoas, casa e imobiliário, objetos, símbolos). Cada
 * `name` junta o nome e sinónimos separados por espaços; a pesquisa é feita
 * sem acentos e em minúsculas (`normalizeSearch`), por isso "imovel" encontra
 * "imóvel". Só emojis de um código (ou com o seletor de variação U+FE0F), que
 * renderizam em Windows 10, macOS, iOS e Android sem sequências ZWJ.
 *
 * Este ficheiro e `chat-emoji-picker` são copiados tal como estão para a PWA.
 */

export type ChatEmojiCategoryId = 'smileys' | 'gestures' | 'home' | 'objects' | 'symbols';

export interface ChatEmoji {
  /** O emoji em si. */
  c: string;
  /** Nome e sinónimos em PT, para a pesquisa. */
  n: string;
}

export interface ChatEmojiCategory {
  id: ChatEmojiCategoryId;
  emojis: ChatEmoji[];
}

/** Textos do seletor. A PWA e o CRM passam os seus; estes são a omissão. */
export interface ChatEmojiPickerLabels {
  title: string;
  search: string;
  empty: string;
  recent: string;
  close: string;
  categories: { [id: string]: string };
}

export const CHAT_EMOJI_LABELS_PT: ChatEmojiPickerLabels = {
  title: 'Emojis',
  search: 'Procurar emoji',
  empty: 'Nenhum emoji com esse nome.',
  recent: 'Recentes',
  close: 'Fechar',
  categories: {
    smileys: 'Sorrisos',
    gestures: 'Gestos e pessoas',
    home: 'Casa e imobiliário',
    objects: 'Objetos',
    symbols: 'Símbolos'
  }
};

/** Ícone de cada separador: o primeiro emoji representativo da categoria. */
export const CHAT_EMOJI_CATEGORY_ICONS: { [id: string]: string } = {
  recent: '🕐',
  smileys: '😀',
  gestures: '👍',
  home: '🏠',
  objects: '📎',
  symbols: '❤️'
};

export const CHAT_EMOJI_CATEGORIES: ChatEmojiCategory[] = [
  {
    id: 'smileys',
    emojis: [
      { c: '😀', n: 'sorriso feliz' },
      { c: '😃', n: 'sorriso grande alegre' },
      { c: '😄', n: 'sorriso olhos fechados' },
      { c: '😁', n: 'sorriso radiante dentes' },
      { c: '😆', n: 'riso gargalhada' },
      { c: '😅', n: 'riso suor alívio' },
      { c: '🤣', n: 'rir a rebolar' },
      { c: '😂', n: 'rir lágrimas' },
      { c: '🙂', n: 'sorriso leve' },
      { c: '🙃', n: 'ao contrário brincadeira' },
      { c: '😉', n: 'piscar olho' },
      { c: '😊', n: 'sorriso corado simpático' },
      { c: '😇', n: 'anjo santo' },
      { c: '🥰', n: 'apaixonado corações' },
      { c: '😍', n: 'olhos coração adorar' },
      { c: '🤩', n: 'estrelas olhos fantástico' },
      { c: '😘', n: 'beijo' },
      { c: '😋', n: 'delícia saboroso' },
      { c: '😎', n: 'óculos de sol fixe' },
      { c: '🤓', n: 'nerd óculos' },
      { c: '🧐', n: 'monóculo curioso' },
      { c: '🤗', n: 'abraço' },
      { c: '🤭', n: 'mão na boca ups' },
      { c: '🤫', n: 'silêncio segredo' },
      { c: '🤔', n: 'a pensar dúvida' },
      { c: '🤨', n: 'sobrancelha cético' },
      { c: '😐', n: 'neutro sério' },
      { c: '😑', n: 'sem expressão' },
      { c: '😶', n: 'sem palavras' },
      { c: '🙄', n: 'revirar olhos' },
      { c: '😏', n: 'malandro sorriso lado' },
      { c: '😌', n: 'aliviado calmo' },
      { c: '😴', n: 'a dormir sono' },
      { c: '😷', n: 'máscara doente' },
      { c: '🤒', n: 'doente febre termómetro' },
      { c: '🥳', n: 'festa parabéns' },
      { c: '🥺', n: 'suplicante por favor' },
      { c: '😢', n: 'lágrima triste' },
      { c: '😭', n: 'choro' },
      { c: '😤', n: 'bufar frustrado' },
      { c: '😠', n: 'zangado' },
      { c: '😡', n: 'furioso' },
      { c: '🤯', n: 'cabeça explodir espanto' },
      { c: '😱', n: 'grito medo' },
      { c: '😳', n: 'corado envergonhado' },
      { c: '🥱', n: 'bocejo cansado' },
      { c: '😬', n: 'careta constrangido' },
      { c: '🤝', n: 'aperto de mão acordo negócio' }
    ]
  },
  {
    id: 'gestures',
    emojis: [
      { c: '👍', n: 'gosto polegar cima ok' },
      { c: '👎', n: 'não gosto polegar baixo' },
      { c: '👌', n: 'ok perfeito' },
      { c: '✌️', n: 'vitória paz' },
      { c: '🤞', n: 'dedos cruzados sorte' },
      { c: '🤟', n: 'amor gesto' },
      { c: '🤘', n: 'rock' },
      { c: '🤙', n: 'liga-me telefonar' },
      { c: '👈', n: 'apontar esquerda' },
      { c: '👉', n: 'apontar direita' },
      { c: '👆', n: 'apontar cima' },
      { c: '👇', n: 'apontar baixo' },
      { c: '☝️', n: 'indicador atenção' },
      { c: '✋', n: 'mão levantada parar' },
      { c: '🖐️', n: 'mão aberta cinco' },
      { c: '👋', n: 'acenar olá adeus' },
      { c: '🤚', n: 'costas da mão' },
      { c: '🙌', n: 'celebrar mãos ao ar' },
      { c: '👏', n: 'aplausos palmas' },
      { c: '🤲', n: 'palmas juntas oferecer' },
      { c: '🙏', n: 'obrigado por favor rezar' },
      { c: '💪', n: 'força músculo' },
      { c: '✍️', n: 'escrever assinar contrato' },
      { c: '👀', n: 'olhos ver atenção' },
      { c: '🧑', n: 'pessoa' },
      { c: '👤', n: 'silhueta utilizador' },
      { c: '👥', n: 'pessoas grupo' },
      { c: '👪', n: 'família' },
      { c: '👫', n: 'casal' },
      { c: '👶', n: 'bebé' },
      { c: '🧒', n: 'criança' },
      { c: '🧓', n: 'idoso sénior' },
      { c: '🙋', n: 'levantar mão pergunta' },
      { c: '🙆', n: 'ok pessoa braços' },
      { c: '🙅', n: 'não recusar' },
      { c: '🤷', n: 'encolher ombros não sei' },
      { c: '🤦', n: 'mão na testa' },
      { c: '🚶', n: 'a andar caminhar' },
      { c: '🏃', n: 'a correr pressa' },
      { c: '💃', n: 'dançar festa' }
    ]
  },
  {
    id: 'home',
    emojis: [
      { c: '🏠', n: 'casa lar habitação imóvel moradia' },
      { c: '🏡', n: 'casa com jardim moradia quinta' },
      { c: '🏘️', n: 'casas bairro urbanização condomínio' },
      { c: '🏢', n: 'prédio escritórios edifício apartamento' },
      { c: '🏬', n: 'loja centro comercial' },
      { c: '🏭', n: 'fábrica armazém industrial' },
      { c: '🏗️', n: 'obra construção grua' },
      { c: '🏚️', n: 'casa abandonada ruína recuperar' },
      { c: '🏛️', n: 'edifício clássico histórico' },
      { c: '🏨', n: 'hotel turismo' },
      { c: '🏦', n: 'banco crédito financiamento' },
      { c: '🏪', n: 'loja comércio' },
      { c: '🏫', n: 'escola' },
      { c: '🏥', n: 'hospital saúde' },
      { c: '⛪', n: 'igreja' },
      { c: '🏰', n: 'castelo palácio' },
      { c: '🏙️', n: 'cidade skyline centro' },
      { c: '🌇', n: 'pôr do sol cidade' },
      { c: '🌃', n: 'noite cidade' },
      { c: '🏞️', n: 'parque natureza campo' },
      { c: '🏖️', n: 'praia férias' },
      { c: '🏔️', n: 'montanha serra' },
      { c: '🌊', n: 'mar onda vista' },
      { c: '🌳', n: 'árvore jardim' },
      { c: '🌴', n: 'palmeira' },
      { c: '🌻', n: 'girassol flor' },
      { c: '🌷', n: 'tulipa flor' },
      { c: '🚗', n: 'carro garagem' },
      { c: '🚙', n: 'jipe carro' },
      { c: '🚕', n: 'táxi' },
      { c: '🚌', n: 'autocarro transportes' },
      { c: '🚆', n: 'comboio estação' },
      { c: '🚇', n: 'metro' },
      { c: '🚲', n: 'bicicleta' },
      { c: '✈️', n: 'avião aeroporto viagem' },
      { c: '🅿️', n: 'estacionamento parque' },
      { c: '🛏️', n: 'cama quarto dormir' },
      { c: '🛋️', n: 'sofá sala estar' },
      { c: '🚿', n: 'duche chuveiro casa de banho' },
      { c: '🛁', n: 'banheira casa de banho' },
      { c: '🚪', n: 'porta entrada' },
      { c: '🔑', n: 'chave chaves entrega' },
      { c: '🗝️', n: 'chave antiga' },
      { c: '🔒', n: 'fechado trancado segurança' },
      { c: '🔓', n: 'aberto destrancado' },
      { c: '🏊', n: 'piscina natação' },
      { c: '🧱', n: 'tijolo obras parede' },
      { c: '🔨', n: 'martelo obras remodelação' },
      { c: '🛠️', n: 'ferramentas reparação' },
      { c: '🌡️', n: 'termómetro temperatura' },
      { c: '☀️', n: 'sol solarengo luz' },
      { c: '🌤️', n: 'sol nuvens tempo' },
      { c: '🌧️', n: 'chuva' },
      { c: '❄️', n: 'neve frio' },
      { c: '🔥', n: 'fogo lareira quente' },
      { c: '💡', n: 'luz lâmpada ideia' },
      { c: '🔌', n: 'eletricidade ficha' },
      { c: '📐', n: 'régua esquadro planta' },
      { c: '📏', n: 'régua medida metros área' },
      { c: '🧭', n: 'bússola orientação' },
      { c: '🗺️', n: 'mapa localização' },
      { c: '📍', n: 'localização pin morada' },
      { c: '🧹', n: 'limpeza vassoura' }
    ]
  },
  {
    id: 'objects',
    emojis: [
      { c: '📱', n: 'telemóvel' },
      { c: '☎️', n: 'telefone' },
      { c: '📞', n: 'chamada telefonar' },
      { c: '💻', n: 'portátil computador' },
      { c: '🖥️', n: 'computador ecrã' },
      { c: '📧', n: 'email correio' },
      { c: '✉️', n: 'envelope carta' },
      { c: '📨', n: 'mensagem recebida' },
      { c: '📩', n: 'enviar mensagem' },
      { c: '📎', n: 'clip anexo' },
      { c: '📌', n: 'pin fixar' },
      { c: '📝', n: 'nota apontamento' },
      { c: '📄', n: 'documento página' },
      { c: '📃', n: 'página enrolada' },
      { c: '📑', n: 'separadores marcadores' },
      { c: '📋', n: 'prancheta lista checklist' },
      { c: '📁', n: 'pasta ficheiros' },
      { c: '📂', n: 'pasta aberta' },
      { c: '🗂️', n: 'arquivo separadores' },
      { c: '📊', n: 'gráfico barras' },
      { c: '📈', n: 'gráfico subida crescimento' },
      { c: '📉', n: 'gráfico descida' },
      { c: '💰', n: 'dinheiro saco' },
      { c: '💶', n: 'euro notas dinheiro' },
      { c: '💳', n: 'cartão crédito pagamento' },
      { c: '🧾', n: 'recibo fatura' },
      { c: '🏷️', n: 'etiqueta preço' },
      { c: '💼', n: 'mala trabalho negócios' },
      { c: '📅', n: 'calendário data' },
      { c: '📆', n: 'calendário dia' },
      { c: '🗓️', n: 'agenda marcação' },
      { c: '⏰', n: 'despertador alarme' },
      { c: '⌚', n: 'relógio hora' },
      { c: '⏳', n: 'ampulheta espera tempo' },
      { c: '📷', n: 'câmara fotografia' },
      { c: '📸', n: 'fotografia flash' },
      { c: '🎥', n: 'vídeo câmara filmar' },
      { c: '🎬', n: 'claquete vídeo' },
      { c: '🔍', n: 'lupa procurar pesquisar' },
      { c: '🔎', n: 'lupa direita' },
      { c: '✏️', n: 'lápis escrever' },
      { c: '🖊️', n: 'caneta assinar' },
      { c: '✂️', n: 'tesoura cortar' },
      { c: '📦', n: 'caixa encomenda mudança' },
      { c: '🎁', n: 'presente oferta' },
      { c: '🎉', n: 'festa parabéns celebração' },
      { c: '🎊', n: 'confetti festa' },
      { c: '🏆', n: 'troféu vencedor' },
      { c: '🥂', n: 'brinde champanhe celebrar' },
      { c: '🍾', n: 'champanhe garrafa' },
      { c: '☕', n: 'café reunião' },
      { c: '🛒', n: 'carrinho compras' },
      { c: '🔔', n: 'sino notificação' },
      { c: '🔕', n: 'sino silêncio' },
      { c: '📣', n: 'megafone anúncio' },
      { c: '📢', n: 'altifalante aviso' }
    ]
  },
  {
    id: 'symbols',
    emojis: [
      { c: '❤️', n: 'coração vermelho amor' },
      { c: '🧡', n: 'coração laranja' },
      { c: '💛', n: 'coração amarelo' },
      { c: '💚', n: 'coração verde' },
      { c: '💙', n: 'coração azul' },
      { c: '💜', n: 'coração roxo' },
      { c: '🖤', n: 'coração preto' },
      { c: '🤍', n: 'coração branco' },
      { c: '💔', n: 'coração partido' },
      { c: '💯', n: 'cem por cento' },
      { c: '✅', n: 'certo feito confirmado' },
      { c: '☑️', n: 'caixa marcada check' },
      { c: '✔️', n: 'visto check' },
      { c: '❌', n: 'cruz errado cancelar' },
      { c: '❓', n: 'pergunta dúvida' },
      { c: '❗', n: 'exclamação importante' },
      { c: '⚠️', n: 'aviso atenção' },
      { c: '🚫', n: 'proibido não' },
      { c: '🔴', n: 'círculo vermelho' },
      { c: '🟠', n: 'círculo laranja' },
      { c: '🟡', n: 'círculo amarelo' },
      { c: '🟢', n: 'círculo verde' },
      { c: '🔵', n: 'círculo azul' },
      { c: '⚪', n: 'círculo branco' },
      { c: '⚫', n: 'círculo preto' },
      { c: '⭐', n: 'estrela favorito' },
      { c: '🌟', n: 'estrela brilhante' },
      { c: '✨', n: 'brilho novo' },
      { c: '⚡', n: 'raio rápido' },
      { c: '🔝', n: 'topo' },
      { c: '🔜', n: 'em breve' },
      { c: '🆕', n: 'novo' },
      { c: '🆗', n: 'ok' },
      { c: '🆓', n: 'grátis' },
      { c: '➡️', n: 'seta direita' },
      { c: '⬅️', n: 'seta esquerda' },
      { c: '⬆️', n: 'seta cima' },
      { c: '⬇️', n: 'seta baixo' },
      { c: '🔁', n: 'repetir' },
      { c: '🔄', n: 'atualizar rodar' },
      { c: '♻️', n: 'reciclar sustentável' },
      { c: '➕', n: 'mais adicionar' },
      { c: '➖', n: 'menos' },
      { c: '✖️', n: 'multiplicar vezes' },
      { c: '➗', n: 'dividir' },
      { c: '♾️', n: 'infinito' },
      { c: '🔗', n: 'ligação link' },
      { c: '💬', n: 'balão fala mensagem' },
      { c: '💭', n: 'pensamento' },
      { c: '🕐', n: 'hora relógio' },
      { c: '🚩', n: 'bandeira marcar' },
      { c: '🏁', n: 'chegada meta' },
      { c: '⚖️', n: 'balança justiça contrato' },
      { c: '🔐', n: 'cadeado chave seguro' }
    ]
  }
];
